/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { get, flatten } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, FormLabel, Image, InputGroup, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import Button from '../../../../shared/components/Button';
import Checkbox from '../../../../shared/components/Checkbox/Checkbox';

/**
 * Check if a reward is included on the form and have options to redeem.
 * Also if the actual index that is working on is passed checks start from there to check
 * @param {Reward} r Object with the reward settings
 * @param {number} index Index of reward
 * @param {number} offset Offset to point current reward
 * @returns
 */
export const rewardHasOptionsToRedeem = (r) => {
  const hasRewards = r.include_in_participation_form &&
    get(r, 'reward_questions.length', false) &&
    r.reward_questions.every(question => get(question, 'reward_options.length', 0) > 0);

  return hasRewards;
};

const validationSchema = Yup.object({
  questions: Yup.array(),
});

const SelectReward = ({
  onRequestClose,
  prevStep,
  nextStep,
  lastStep
}) => {
  const { t } = useTranslation();
  /** @type {import('../../../../reducers/offer.reducer').Offer} */
  const offer = useSelector(store => get(store, 'offer.offer'));
  const { user_offer: userOffer } = offer;
  const { rewards } = offer;
  const oauthSettings = useSelector(store => get(store, 'offer.oAuthSettings'));
  const testFlow = useSelector(store => get(store, 'config.test'));

  const [state, setState] = useState({
    loading: false,
    questions: [],
    activeQuestionIndex: 0
  });

  const currentQuestion = useMemo(() => state.questions[state.activeQuestionIndex],
    [state.questions, state.activeQuestionIndex]);

  const facebookOauthOptional = testFlow.includes('fb_optional')
    ? true
    : oauthSettings.facebook.optional || false;
  const showConnectFacebook = facebookOauthOptional &&
    !get(userOffer, 'facebook_oauth_token');

  const formik = useFormik({
    initialValues: {
      questions: [],
    },
    validationSchema,
    onSubmit: values => {
      if (testFlow.some(t => ['fb_required', 'fb_optional'].includes(t))) {
        nextStep({
          connectFacebook: true,
        });
      } else {
        if (showConnectFacebook) return onSaveInfo(values, false);
        onSaveInfo(values, !showConnectFacebook);
      }
    },
  });

  useEffect(() => {
    // Transforms an array of rewards into an object indexed by its ID
    // to save options to redeem. This is for formik
    const questions = rewards
      .filter(reward =>
        reward.include_in_participation_form &&
        get(reward, 'reward_questions.length', false) &&
        reward.reward_questions.every(question => get(question, 'reward_options.length'))
      ).flatMap(reward =>
        reward.reward_questions.map(rq => {
          const { options_to_choose, question } = rq;
          let options = [];

          options = rq.reward_options.map(rw => ({
            ...rw,
            checked: false,
            rank: null,
          }));

          return {
            options,
            rewardId: reward.id,
            questionId: rq.id,
            rewardOptionId: rq.reward_option_id,
            optionsToChoose: options_to_choose,
            question,
            desc: reward.desc
          };
        })
      );

    formik.setFieldValue('questions', questions);

    const activeQuestionIndex = 0;

    setState({
      ...state,
      questions,
      activeQuestionIndex,
    });
  }, [rewards]);


  const sendReward = useCallback((reward_request) =>
    axios.post(`/api/v1/influencers/user_reward_requests?user_offer_id=${userOffer.id}`, {
      user_reward_request: reward_request
    })
    , [userOffer.id]);

  const onSaveInfo = (values, submit = false) => {

    const { questions } = values;
    const questionsPromises = [];

    questions.forEach(question => {
      const user_reward_request = {
        reward_id: +question.rewardId,
        rank: 1,
        reward_option_id: null
      };

      if (question.optionsToChoose == 1) {
        user_reward_request.reward_option_id = +question.option;
        questionsPromises.push(sendReward({ ...user_reward_request }));
      } else {
        question.options.forEach(option => {
          if (!option.checked) return;

          user_reward_request.rank = option.rank;
          user_reward_request.reward_option_id = option.id;
          questionsPromises.push(sendReward({ ...user_reward_request }));
        });
      }
    });

    Promise.allSettled(questionsPromises)
      .then(responses => {
        if (submit && responses.every(response => get(response, 'value.data.success') || false)) {
          return nextStep();
        } else if (showConnectFacebook) {
          nextStep({
            connectFacebook: true,
            userOfferTmp: {
              id: userOffer.id,
              user_offer_linked_talent_agents: []
            }
          });
        }
        onRequestClose({ savedApplication: true });
      });
  };

  const prevQuestion = useCallback(() => {
    const { activeQuestionIndex } = state;

    const prevActiveQuestionIndex = activeQuestionIndex - 1;

    if (activeQuestionIndex > 0) {
      const checkedOptions = flatten(
        formik.values.questions.map(
          (question) => question.options.filter(
            (option) => option.checked || question.option == option.id
          )
        )
      );

      const tempQuestions = formik.values.questions.map((question) => ({
        show: question.rewardOptionId == null || checkedOptions.filter(
          (option) => {
            const radioChecked = option.id == question.rewardOptionId;
            const checkboxChecked = option.checked && option.id == question.rewardOptionId;

            return checkboxChecked || radioChecked;
          }
        ).length > 0
      }));

      setState({
        ...state,
        activeQuestionIndex: tempQuestions[prevActiveQuestionIndex].show ? prevActiveQuestionIndex : activeQuestionIndex - 2
      });
    } else {
      prevStep();
    }
  }, [state]);

  const nextQuestion = useCallback(() => {
    const { activeQuestionIndex, questions } = state;
    const nextActiveQuestionIndex = activeQuestionIndex + 1;

    if (activeQuestionIndex < questions.length - 1) {
      const checkedOptions = flatten(
        formik.values.questions.map(
          (question) => question.options.filter(
            (option) => option.checked || question.option == option.id
          )
        )
      );

      const tempQuestions = formik.values.questions.map((question) => ({
        show: question.rewardOptionId == null || checkedOptions.filter(
          (option) => {
            const radioChecked = option.id == question.rewardOptionId;
            const checkboxChecked = option.checked && option.id == question.rewardOptionId;

            return checkboxChecked || radioChecked;
          }
        ).length > 0
      }));

      setState({
        ...state,
        activeQuestionIndex: tempQuestions[nextActiveQuestionIndex].show ? nextActiveQuestionIndex : activeQuestionIndex + 2
      });
    } else {
      formik.submitForm();
    }
  }, [state, formik.values]);

  const onChangeCheckbox = useCallback((e) => {
    const { activeQuestionIndex } = state;
    const { name } = e.target;
    const value = get(formik.values, name);
    const { options } = formik.values.questions[activeQuestionIndex];

    // remove rank and assign new index ranks
    if (value.checked) {
      const { rank } = value;
      options.map(option => {
        if (option.rank > rank) {
          option.rank = option.rank - 1;
          option.checked = true;
        }
      });
      formik.setFieldValue(name, {
        ...value,
        checked: false,
        rank: null
      });
    } else {
      const checkedElements = options.reduce((count, curr) => count + Number(Boolean(curr.checked)), 0);
      formik.setFieldValue(name, {
        ...value,
        checked: true,
        rank: checkedElements + 1
      });
    }
  }, [formik.values, state.activeQuestionIndex]);

  const renderCheckboxOptions = (option, optionIndex) => {
    const { activeQuestionIndex } = state;
    const name = `questions.${activeQuestionIndex}.options.${optionIndex}`;
    const value = get(formik.values, name);
    return <Checkbox
      name={name}
      value={get(value, 'checked')}
      onChange={onChangeCheckbox}
      disabled={state.loading || (checkedElements === currentQuestion.optionsToChoose && !get(value, 'checked'))}
      label={
        <Row className="text-1">
          <Col xs={10}>
            {option.label}
            {option.pic && <Image src={option.pic} alt={option.label} fluid loading='lazy' />}
          </Col>
          <Col xs={2}>
            {get(value, 'rank')}
          </Col>
        </Row>
      }
      tabIndex={"auto"}
    />;
  };

  const renderRadioOptions = (option, optionIndex) => {
    const { activeQuestionIndex } = state;
    const checked = get(formik.values, `questions.${activeQuestionIndex}.option`) == option.id;

    return <React.Fragment>
      <input
        type="radio"
        name={`questions.${activeQuestionIndex}.option`}
        data-cy="reward_option"
        value={option.id}
        checked={checked}
        onChange={formik.handleChange}
        disabled={state.loading}
        tabIndex={"auto"}
      />
      <FormLabel htmlFor={option.id}>
        {option.label}
        {option.pic && <Image src={option.pic} alt={option.label} fluid loading='lazy' />}
      </FormLabel>
    </React.Fragment>;
  };

  /**
   * Render options for current reward
   */
  const renderCurrentOptions = useCallback(() =>
    currentQuestion.options
      .map((option, index) =>
        <InputGroup className="mb-3" key={option.id}>
          {currentQuestion.optionsToChoose > 1
            ? renderCheckboxOptions(option, index)
            : renderRadioOptions(option, index)
          }
        </InputGroup>
      ), [currentQuestion, formik]);

  if (!currentQuestion) return null;

  let checkedElements = 0;
  const activeQuestionFormik = get(formik.values, `questions.${state.activeQuestionIndex}`) || {};
  if (currentQuestion.optionsToChoose > 1) {
    const optionsOfActiveQuestion = activeQuestionFormik.options || [];
    checkedElements = optionsOfActiveQuestion.reduce((count, curr) => count + Boolean(curr.checked), 0);
  } else {
    checkedElements = Number(!!activeQuestionFormik.option);
  }

  return (<React.Fragment>
    <h3 className='mb-4' >
      {t('influencers.pages.campaign.apply_side_pane.product_selection')}
    </h3>
    <div className='body-1 mb-2' dangerouslySetInnerHTML={{ __html: currentQuestion.desc }}>
    </div>

    <Form onSubmit={formik.handleSubmit}>
      <div className='body-1'
        dangerouslySetInnerHTML={{ __html: get(currentQuestion, 'question', '') }} />
      {/* Options to select message */}
      <div className='mb-3'>
        {
          currentQuestion.optionsToChoose > 1 ?
            <small className="mb-5">Select {currentQuestion.optionsToChoose} options</small>
            : null
        }
      </div>
      {/* Form of the current question */}
      {renderCurrentOptions()}
      <hr className='my-6' />
      <Row className='justify-content-end'>
        <Col
          className="px-sm-2"
          xs={{
            span: "auto",
            order: 4
          }} sm={{
            span: "auto",
            order: 1
          }}>
          <Button
            variant="tertiary"
            type="button"
            onClick={() => onRequestClose()}
            disabled={state.loading}>
            {t('influencers.common.cancel')}
          </Button>
        </Col>
        <Col
          className="px-sm-2 mb-2"
          xs={{
            span: "auto",
            order: 1
          }}
          sm={{
            span: "auto",
            order: 2
          }}
        >
          {/*
          <Button
            variant="secondary"
            type="button"
            onClick={() => onSaveInfo(formik.values)}
            disabled={state.loading}>
            {t('influencers.common.save')}
          </Button>
          */}
        </Col>
        <Col
          className="px-sm-2"
          xs={{
            span: "auto",
            order: 2
          }}
          sm={{
            span: "auto",
            order: 3
          }}>
          <Button
            variant="secondary"
            type="button"
            onClick={prevQuestion}
            disabled={state.loading}>
            {t('influencers.common.back')}
          </Button>
        </Col>
        <Col
          className="px-sm-2"
          xs={{
            span: "auto",
            order: 3
          }} sm={{
            span: "auto",
            order: 4
          }}>
          <Button
            idButton="offer-application-submit"
            lastStep={lastStep()}
            variant="primary"
            type="button"
            onClick={nextQuestion}
            disabled={state.loading || currentQuestion.optionsToChoose !== checkedElements}>
            {state.loading
              ? <Spinner animation="border" size="sm" />
              : t('influencers.common.submit')
            }
          </Button>
        </Col>
      </Row>

    </Form>
  </React.Fragment>);
};

SelectReward.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  lastStep: PropTypes.func.isRequired
};

export default SelectReward;


/**
 * @typedef RewardOption
 * @property {number} id
 * @property {number} offer_id
 * @property {number} reward_id
 * @property {number} reward_question_id
 * @property {string} label
 * @property {string} pic
 * @property {string} pic_thumb_800_if_ready
 * @property {string} pic_thumb_400_if_ready
 * @property {string} pic_thumb_100_if_ready
 * @property {string} url
 *
 *
 * @typedef RewardQuestion
 * @property {number} id
 * @property {number} offer_id
 * @property {number} reward_id
 * @property {string} question
 * @property {number} options_to_choose
 * @property {RewardOption[]} reward_options
 *
 * @typedef Reward
 * @property {number} id
 * @property {string} name
 * @property {string} name
 * @property {string} desc
 * @property {string} pic
 * @property {string} rtype
 * @property {number} value
 * @property {boolean} phone_number_required
 * @property {boolean} payment_email_required
 * @property {boolean} address_required
 * @property {boolean} include_in_participation_form
 * @property {RewardQuestion[]} reward_questions
 */
